<template>
  <div class="p-the-template-j">
    <TheBreadcrumb />
    <div class="p-the-template-j__inner">
      <div class="p-the-template-j__header">
        <slot name="header">
          <CommonHeading v-if="heading"><span data-translate-skip>{{ heading }}</span>メッセージ</CommonHeading>
        </slot>
      </div>
      <div class="p-the-template-j__body">
        <div class="p-the-template-j__body__header">
          <div class="p-search-area">
            <div
              v-if="indicateSearchForm"
              ref="searchFormElement"
              class="p-the-template-j__search"
              :class="executeSearchFormDomAnimation ? searchFormClassName : ''"
              :style="executeSearchFormDomAnimation ? serchFormStyle : {}"
            >
              <FormSearch
                v-model:search="search"
                :placeholder="searchPlaceholder"
                @do-search="doSearch"
              />
            </div>
            <slot name="filter" />
          </div>
          <template v-if="indicateNewAddButton">
            <div class="p-the-template-j__new-add">
              <Portal to="j-buttons">
                <ButtonMain class="p-the-template-j__give-button" tag="a" kind="main" :href="url">
                  新しく<span data-translate-skip>{{ thanksPointName }}</span>を贈る
                </ButtonMain>
              </Portal>
            </div>
          </template>
          <div class="p-the-template-j__right-col">
            <div
              v-if="indicateSearchButton"
              class="p-the-template-j__shown-search-form-button"
            >
              <button
                class="p-the-template-j__shown-search-form-button__button"
                @click="switchSearchFormState"
              >
                <ButtonRefineBase>ユーザー検索</ButtonRefineBase>
              </button>
            </div>

            <div v-if="indicateSortedButton" class="p-the-template-j__sorted">
              <ButtonRefine
                v-model:refine="sorted.target"
                :heading="filterHeading"
                icon="sorted"
                :refine-item-list="sorted.itemList"
                :shown="sorted.shownState"
                :width="widthRefineButton"
                @do-sort="doSort($event)"
                @switch-refine-state="switchSortedState"
              />
            </div>
          </div>
        </div>
        <div>
          <ValidationErrorText v-show="errorMessage">
            {{ errorMessage }}
          </ValidationErrorText>
        </div>
        <div
          ref="contentElement"
          class="p-the-template-j__body__content"
          :style="contentElementStyle"
        >
          <slot name="contents" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { gsap } from "gsap";
import TheStickyFooterButton from "@/common/components/organisms/TheStickyFooterButton.vue";
import FormSearch from "@/common/components/atoms/FormSearch.vue";
import ButtonRefine from "@/common/components/atoms/ButtonRefine.vue";
import ButtonRefineBase from "@/common/components/atoms/ButtonRefineBase.vue";
import ValidationErrorText from "@/common/components/atoms/ValidationErrorText.vue";
import { Options, Vue } from "vue-class-component";
import globals from "@/general/store/modules/globals";
import modalState from "@/common/store/modules/modalState";
import { getThanksPointName } from "@/common/assets/js/utils/pointName";
import SITE_MAP from "@/general/mixins/const/custom/sitemap";
import CONST_DATA from "@/general/mixins/const/constData";

@Options({
  props: {
    heading: { type: [String, Boolean], default: false, required: true },
    indicatedItem: { type: Array, default: ["search", "button"] },
    searchPlaceholder: { type: String, default: "やり取り相手を探す" },
    initShownSearchFormState: { type: Boolean, default: false },
    sorted: {
      type: Object,
      default: () => ({
        target: "username-asc",
        shownState: false,
        itemList: []
      })
    },
    filterHeading: { type: String, default: "並べ替え" },
    errorMessage: { type: String, default: null }
  },
  components: {
    TheStickyFooterButton,
    FormSearch,
    ButtonRefine,
    ButtonRefineBase,
    ValidationErrorText
  },
  watch: {
    isSp(newVal: boolean) {
      this.initSearchFormDomAnimation();
    },
    shownSearchFormState(newVal: boolean) {
      this.animationSearchFormDom(this.$refs.searchFormElement);
    }
  }
})
export default class TheTemplateJ extends Vue {
  declare $refs: {
    contentElement: HTMLElement;
    searchFormElement: HTMLElement;
  };

  heading!: string;
  indicatedItem!: Array<string>;
  searchPlaceholder!: string;
  initShownSearchFormState!: string;
  sorted!: any;
  filterHeading!: string;
  errorMessage!: string;

  searchKeyword  = "";
  isSp = globals.isSpScreenWidth;
  closeModalState = modalState.closeModalState;

  fadeOutState = false;
  searchUriBase = ["/j03/", "/j04/"];

  executeSearchFormDomAnimation = false;
  shownSearchFormState = true;
  searchFormClassName = "p-the-template-j__search--animation";

  serchFormStyle = {
    visibility: "hidden",
    opacity: 0
  };

  get url() {
    return SITE_MAP.THANKS_POINT_GIVE_THANKS_POINT;
  }

  get search() {
    return this.searchKeyword;
  }

  set search(value) {
    this.searchKeyword = value;
  }

  get indicateSearchForm() {
    const indicatedItem = this.indicatedItem;
    const keyword = "search";
    return indicatedItem.includes(keyword);
  }

  get indicateSearchButton() {
    const indicatedItem = this.indicatedItem;
    return indicatedItem.includes("searchButton");
  }

  get indicateSortedButton() {
    const indicatedItem = this.indicatedItem;
    const keyword = "sorted";
    return indicatedItem.includes(keyword);
  }

  get contentElementStyle() {
    const executeSearchFormDomAnimation = this.executeSearchFormDomAnimation;
    let styleObject = {};

    if (executeSearchFormDomAnimation === false) {
      styleObject = {
        marginTop: 0
      };
    }

    return styleObject;
  }

  get indicateNewAddButton() {
    const indicatedItem = this.indicatedItem;
    const keyword = "button";
    return indicatedItem.includes(keyword);
  }

  get widthRefineButton() {
    return "170px";
  }

  get thanksPointName() {
    return getThanksPointName(CONST_DATA.POINT_NAME.THANKS_POINT_DEFAULT_NAME1);
  }

  mounted() {
    this.initDomElement();
  }

  initDomElement() {
    const initShownSearchFormState = this.initShownSearchFormState;
    this.initSearchFormDomAnimation();

    if (initShownSearchFormState) {
      this.animationSearchFormDom(this.$refs.searchFormElement);
    }
  }

  fadeOutNewAddButton() {
    const scrollY = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const triggerPosition = documentHeight - windowHeight;

    this.fadeOutState = scrollY === triggerPosition;
  }

  doSearch() {
    this.$emit("do-search", this.search);
  }

  doSort($event: any) {
    this.$emit("do-sort", $event);
  }

  switchSortedState(value: any) {
    this.sorted.shownState = value;
  }

  switchSearchFormState() {
    this.shownSearchFormState = !this.shownSearchFormState;
  }

  initSearchFormDomAnimation() {
    const isSp = this.isSp;
    const indicateSearchButton = this.indicateSearchButton;
    const executeSearchFormDomAnimation = isSp && indicateSearchButton;

    this.executeSearchFormDomAnimation = executeSearchFormDomAnimation;
    this.shownSearchFormState = !executeSearchFormDomAnimation;
  }

  animationSearchFormDom(el: any, shown = this.shownSearchFormState) {
    const executeSearchFormDomAnimation = this.executeSearchFormDomAnimation;
    const { contentElement } = this.$refs;
    const speed = 0.2;

    const animationStyle = {
      formSearchElement: {
        autoAlpha: shown ? 1 : 0
      },
      contentElement: {
        marginTop:
          shown && executeSearchFormDomAnimation ? `${60 / 16}rem` : "0rem"
      }
    };

    gsap.to(el, speed, animationStyle.formSearchElement);
    gsap.to(contentElement, speed, animationStyle.contentElement);
  }
}
</script>

<style lang="scss" scoped>
.p-the-template-j {
  &__inner {
    @include pc {
      max-width: rem(1110);
      margin: 0 auto;
      padding: rem(16) 0 rem(48);
    }
  }

  &__search {
    @include pc {
      width: rem(288);
      min-width: rem(288);
    }

    @include sp {
      flex: 1 1 auto;
      min-width: 0;
      padding-bottom: 0;
    }

    &--animation {
      @include sp {
        width: 100%;
        position: absolute;
        padding-top: rem(60);
      }
    }
  }

  &__new-add {
    width: rem(240);
    min-width: rem(210);
    margin-left: auto;
  }

  &__right-col {
    @include sp {
      display: flex;
      flex-direction: flex-end;
      justify-content: space-between;
      position: relative;
      z-index: 1;
    }
  }

  &__shown-search-form-button {
    @include pc {
      display: none;
    }
    @include sp {
      width: cal(136, 288);
      min-width: cal(136, 288);
    }

    &__button {
      @include sp {
        display: block;
        width: 100%;
      }
    }
  }

  &__sorted {
    @include pc {
      width: rem(168);
      min-width: rem(168);
    }
    @include sp {
      width: cal(136, 288);
      min-width: cal(136, 288);
    }
  }

  &__body {
    @include pc {
      margin-top: rem(16);
      padding: rem(24) rem(24) rem(48);
    }
    @include sp {
      padding: rem(24) 0 rem(48);
    }
    background-color: $common-white;
    border-radius: rem(4);

    &__header {
      @include pc {
        display: flex;
        justify-content: space-between;
      }
      position: relative;
    }
  }

  &__give-button {
    overflow-wrap: break-word;
  }
}
.p-search-area {
  @include pc {
    display: flex;
    align-items: center;
    gap: rem(12);
  }

  @include sp {
    display: flex;
    align-items: center;
    gap: rem(8);
    margin-bottom: rem(12);
  }

  @media (max-width: 360px) {
    display: block;
  }
}

@media (max-width: 360px) {
  .p-the-template-j__search {
    width: 100%;
  }
}
.p-the-template-j__search {
  @include sp {
    flex: 1 1 auto;
    min-width: 0;
    padding-bottom: 0;
  }
}
</style>
